import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import Highcharts3D from "highcharts/highcharts-3d";
import HighchartsReact from "highcharts-react-official";
import GridLight from "highcharts/themes/grid-light";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import ChartFallbackImg from "./ChartFallbackImg";

GridLight(Highcharts);

function DonutChart({ donutData }) {
  const [loading, setLoading] = useState(true);
  const [chartVisible, setChartVisible] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSentiment, setSelectedSentiment] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) {
          chart.reflow();
        }
      });
    };

    window.addEventListener("resize", handleResize);
    setLoading(false); // Set loading to false when data is ready

    return () => window.removeEventListener("resize", handleResize);
  }, [donutData]);

  const handleDonutClick = (event) => {
    const clickedSentiment = event.point.name;
    setSelectedSentiment(clickedSentiment);
    setIsDrawerOpen(true);
  };

  if (loading) {
    return "";
  }

  if (!donutData || donutData.length === 0) {
    return <ChartFallbackImg titleText="Sentiment Analysis" />;
  }

  const colorMap = {
    negative: "#fa4d56", // Red
    neutral: "#8a3ffc", // Yellow
  };

  const neutralCount = donutData.reduce(
    (acc, data) => acc + (data.neutral || 0),
    0
  );
  const negativeCount = donutData.reduce(
    (acc, data) => acc + (data.negative || 0),
    0
  );

  const categories = ["Neutral", "Negative"];
  const data = [neutralCount, negativeCount];
  const colors = categories.map((category) => colorMap[category.toLowerCase()]);
  
  Highcharts3D(Highcharts);
  const options = {
    chart: {
      type: "pie",
      backgroundColor: "#ffffff",
      options3d: {
        enabled: true,
        alpha: 45,
        beta: 0,
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Sentiment Analysis",
    },
    plotOptions: {
      pie: {
        innerSize: 100,
        depth: 45,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return (
              "<b>" +
              this.point.name +
              ":</b> " +
              ((this.y / (neutralCount + negativeCount)) * 100).toFixed(2) +
              "%"
            );
          },
        },
        point: {
          events: {
            click: handleDonutClick, // Attach event handler here
          },
        },
      },
    },

    series: [
      {
        name: "Sentiment",
        data: categories.map((category, index) => ({
          name: category,
          y: data[index],
          color: colors[index],
        })),
      },
    ],
    tooltip: {
      style: {
        fontSize: "18px",
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            title: {
              style: {
                fontSize: "16px",
              },
            },
            plotOptions: {
              pie: {
                innerSize: 80,
                depth: 30,
                dataLabels: {
                  style: {
                    fontSize: "17px",
                    color: "black",
                  },
                },
                point: {
                  events: {
                    click: handleDonutClick, // Handle bubble clicks
                  },
                },
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      {donutData.length > 0 ? (
        <>
          <HighchartsReact highcharts={Highcharts} options={options} />
          <SwipeableDrawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onOpen={() => setIsDrawerOpen(true)} // 🔧 Add this line
            PaperProps={{
              sx: {
                height: "100%",
                overflow: "hidden",
                borderTopLeftRadius: "12px",
                borderBottomLeftRadius: "12px",
              },
            }}
          >
            <Box
              sx={{
                width: 650,
                padding: 2,
                height: "100%",
                overflow: "hidden",
              }}
            >
              {selectedSentiment && (
                <ChartDatadrawer sentiment={selectedSentiment} />
              )}
            </Box>
          </SwipeableDrawer>
        </>
      ) : (
        <ChartFallbackImg titleText="Sentiment Analysis" />
      )}
    </div>
  );
}

export default DonutChart;
